/**
 * AWARE Health — View Mode Context
 * Toggles between 'simple' (general public) and 'detailed' (expert) views.
 * Default: simple. Persisted to localStorage.
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'

export type ViewMode = 'simple' | 'detailed'

interface ViewModeContextValue {
  viewMode: ViewMode
  isDetailed: boolean
  toggleViewMode: () => void
}

const ViewModeContext = createContext<ViewModeContextValue>({
  viewMode: 'simple',
  isDetailed: false,
  toggleViewMode: () => {},
})

export function useViewMode() {
  return useContext(ViewModeContext)
}

export function ViewModeProvider({ children }: { children: ReactNode }) {
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = localStorage.getItem('aware-view-mode')
    return (stored === 'simple' || stored === 'detailed') ? stored : 'simple'
  })

  useEffect(() => {
    localStorage.setItem('aware-view-mode', viewMode)
  }, [viewMode])

  const toggleViewMode = () => setViewMode((m) => (m === 'simple' ? 'detailed' : 'simple'))

  return (
    <ViewModeContext.Provider value={{ viewMode, isDetailed: viewMode === 'detailed', toggleViewMode }}>
      {children}
    </ViewModeContext.Provider>
  )
}
